import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import mammoth from 'mammoth';
import DOMPurify from 'dompurify';
import { useAuth } from "../contexts/AuthContext";
import "../pages/DocumentView.css";
import { DocumentApi, DocumentMetadataApi, FileApi, EndeavorApi } from '../services/ApiService';
import { DocumentEndeavorApi } from '../services/api/DocumentEndeavorAPI';

// Field order for consistent display
const fieldOrder = [
  "Endeavor",
  "Title",
  "Author",
  "Date",
  "Important Names & Events",
  "Important Names and Events",
  "Key Topics",
  "Summary",
  "Document Text",
  "Document Text Translated from English to Spanish",
  "Notes",
];

// Helper function to parse comma/semicolon-separated values or JSON arrays
function parseAsArray(str) {
  if (!str || typeof str !== "string") return [];
  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch (err) {
    // ignore error, fallback below
  }
  // Custom splitting on commas and semicolons (avoids splitting "March 5, 1770")
  return str
    .split(/;|,(?!\s*\d)/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// Convert array back to string for storage
function arrayToString(arr) {
  if (!Array.isArray(arr)) return "";
  return JSON.stringify(arr);
}

// Tag Editor Component
function TagEditor({ value, onChange, placeholder, fieldName }) {
  const [tags, setTags] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);

  // Initialize tags from value
  useEffect(() => {
    const parsedTags = parseAsArray(value);
    setTags(parsedTags);
  }, [value]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleAddTag = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue && !tags.includes(trimmedValue)) {
      const newTags = [...tags, trimmedValue];
      setTags(newTags);
      onChange(arrayToString(newTags));
      setInputValue("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const newTags = tags.filter(tag => tag !== tagToRemove);
    setTags(newTags);
    onChange(arrayToString(newTags));
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setInputValue("");
    } else if (e.key === ',' && inputValue.trim()) {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      handleAddTag();
    }
    setIsEditing(false);
  };

  return (
    <div className="tag-editor">
      {/* Display existing tags */}
      <div className="tag-container">
        {tags.map((tag, index) => (
          <span key={index} className="tag-pill editable">
            {tag}
            <button
              type="button"
              className="tag-remove"
              onClick={() => handleRemoveTag(tag)}
              title="Remove tag"
            >
              ×
            </button>
          </span>
        ))}
        
        {/* Add new tag input */}
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            className="tag-input"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleInputKeyDown}
            onBlur={handleInputBlur}
            placeholder="Type and press Enter..."
          />
        ) : (
          <button
            type="button"
            className="add-tag-btn"
            onClick={() => setIsEditing(true)}
            title={`Add new ${fieldName.toLowerCase()}`}
          >
            + Add {fieldName === "Key Topics" ? "Topic" : "Name/Event"}
          </button>
        )}
      </div>
      
      {/* Instructions */}
      <small className="tag-instructions">
        Click "+ Add" to add new items, or click "×" to remove them. You can also type and press Enter or comma to add items.
      </small>
    </div>
  );
}

function DocumentEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [doc, setDoc] = useState(null);
  const [metadata, setMetadata] = useState([]);
  const [fileUrl, setFileUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [docxHtml, setDocxHtml] = useState(null);
  const [docxConverting, setDocxConverting] = useState(false);
  // ✅ FIXED: Course assignment state
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]); // Array of course IDs
  const [coursesLoading, setCourseLoading] = useState(false);
  const [originalCourses, setOriginalCourses] = useState([]); // Track original associations

  // Zoom/pan states
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState(null);
  const [dragging, setDragging] = useState(false);
  const previewRef = useRef(null);

  // Refs for cleanup and optimization
  const fileUrlRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  // Cleanup function for blob URLs
  const cleanupFileUrl = useCallback(() => {
    if (fileUrlRef.current) {
      URL.revokeObjectURL(fileUrlRef.current);
      fileUrlRef.current = null;
    }
  }, []);

  // ✅ FIXED: Load courses and existing associations
  useEffect(() => {
    const loadCoursesAndAssociations = async () => {
      if (!user?.id && !user?.userId) return;
      
      try {
        setCourseLoading(true);
        const userId = user.id || user.userId;
        
        console.log('DocumentEdit: Loading courses and associations for user:', userId);
        
        // Load available courses
        const coursesResponse = await EndeavorApi.getEndeavorsByUser(userId);
        if (coursesResponse.ok) {
          const coursesData = await coursesResponse.json();
          const courses = Array.isArray(coursesData) ? coursesData : (coursesData.content || coursesData.data || []);
          console.log('DocumentEdit: Loaded courses:', courses);
          setAvailableCourses(courses);
        } else {
          console.warn('DocumentEdit: Failed to fetch courses:', coursesResponse.status);
          setAvailableCourses([]);
        }
        
        // Load current course associations if document exists
        if (doc?.id) {
          console.log('DocumentEdit: Loading existing course associations for document:', doc.id);
          const associations = await DocumentEndeavorApi.getExistingEndeavorsForDocument(doc.id, userId);
          console.log('DocumentEdit: Current course associations:', associations);
          
          const associatedCourseIds = associations.map(course => 
            (course.endeavorId || course.id || course.endeavor_id).toString()
          );
          
          console.log('DocumentEdit: Setting selected courses:', associatedCourseIds);
          setSelectedCourses(associatedCourseIds);
          setOriginalCourses(associatedCourseIds);
        }
        
      } catch (error) {
        console.error('DocumentEdit: Error loading courses and associations:', error);
        setAvailableCourses([]);
        setSelectedCourses([]);
        setOriginalCourses([]);
      } finally {
        setCourseLoading(false);
      }
    };

    if (user && doc?.id) {
      loadCoursesAndAssociations();
    }
  }, [user, doc?.id]);

  // ✅ FIXED: Course selection handler
  const handleCourseSelectionChange = (courseId, isSelected) => {
    console.log('DocumentEdit: Course selection changed:', courseId, isSelected);
    setSelectedCourses(prev => {
      if (isSelected) {
        return [...prev, courseId.toString()];
      } else {
        return prev.filter(id => id.toString() !== courseId.toString());
      }
    });
  };

  // ✅ SEPARATED: File URL fetching with error isolation
  const fetchFileUrl = useCallback(async (documentId, retryAttempt = 0) => {
    if (!documentId || !isMountedRef.current) return;

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      setFileLoading(true);
      setFileError(null);
      
      console.log(`DocumentEdit: Fetching file for document ${documentId} (attempt ${retryAttempt + 1})`);
      
      const response = await FileApi.downloadFile(documentId);
      
      // Check if request was aborted
      if (signal.aborted) {
        console.log('DocumentEdit: File fetch aborted');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      
      // Check if request was aborted after blob creation
      if (signal.aborted || !isMountedRef.current) {
        console.log('DocumentEdit: File fetch aborted after blob creation');
        return;
      }

      // Clean up previous URL
      cleanupFileUrl();

      // Create new blob URL with correct MIME type
      const getMimeType = (filename) => {
        const ext = filename?.toLowerCase().split('.').pop();
        switch (ext) {
          case 'pdf': return 'application/pdf';
          case 'jpg':
          case 'jpeg': return 'image/jpeg';
          case 'png': return 'image/png';
          case 'gif': return 'image/gif';
          case 'txt': return 'text/plain';
          case 'doc': return 'application/msword';
          case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          default: return 'application/octet-stream';
        }
      };

      const correctMimeType = getMimeType(doc?.name);
      const typedBlob = new Blob([blob], { type: correctMimeType });
      
      const url = URL.createObjectURL(typedBlob);
      fileUrlRef.current = url;
      
      console.log(`DocumentEdit: File URL created successfully:`, {
        filename: doc?.name,
        mimeType: correctMimeType,
        blobSize: typedBlob.size,
        url: url.substring(0, 50) + '...'
      });
      
      // If it's a DOCX file, convert to HTML using mammoth
      const isDocx = correctMimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                     doc?.name?.toLowerCase().endsWith('.docx');
      
      if (isDocx) {
        console.log('DocumentEdit: Converting DOCX to HTML with mammoth.js');
        setDocxConverting(true);
        try {
          const arrayBuffer = await blob.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          
          // Sanitize HTML and strip inline font-size and color styles to use our universal theme
          const sanitizedHtml = DOMPurify.sanitize(result.value, {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 
                          'ul', 'ol', 'li', 'a', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
                          'blockquote', 'code', 'pre', 'hr', 'span', 'div'],
            ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'colspan', 'rowspan'],
            // Strip all inline styles - we'll use CSS for styling
            FORBID_ATTR: ['style']
          });
          
          if (isMountedRef.current) {
            setDocxHtml(sanitizedHtml);
            console.log('DocumentEdit: DOCX converted successfully (inline styles stripped)');
            if (result.messages.length > 0) {
              console.warn('DocumentEdit: Mammoth conversion messages:', result.messages);
            }
          }
        } catch (conversionError) {
          console.error('DocumentEdit: DOCX conversion failed:', conversionError);
          if (isMountedRef.current) {
            setFileError(`Failed to convert DOCX: ${conversionError.message}`);
          }
        } finally {
          if (isMountedRef.current) {
            setDocxConverting(false);
          }
        }
      }
      
      if (isMountedRef.current) {
        setFileUrl(url);
      }
      
    } catch (error) {
      console.error(`DocumentEdit: Error fetching file (attempt ${retryAttempt + 1}):`, error);
      
      if (signal.aborted) {
        console.log('DocumentEdit: Request was aborted');
        return;
      }

      if (isMountedRef.current) {
        setFileError(error.message || 'Failed to load file');
        
        // Retry logic - up to 3 attempts with exponential backoff
        if (retryAttempt < 2) {
          const delay = Math.pow(2, retryAttempt) * 1000; // 1s, 2s, 4s
          console.log(`DocumentEdit: Retrying in ${delay}ms...`);
          setTimeout(() => {
            if (isMountedRef.current) {
              fetchFileUrl(documentId, retryAttempt + 1);
            }
          }, delay);
        }
      }
    } finally {
      if (isMountedRef.current) {
        setFileLoading(false);
      }
    }
  }, [doc?.name, cleanupFileUrl]);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        
        const docResponse = await DocumentApi.getDocument(id);
        if (!docResponse.ok) {
          throw new Error(`Failed to fetch document: ${docResponse.status}`);
        }
        
        const docData = await docResponse.json();
        if (!docData || !docData.id) {
          console.error("Document not found:", docData);
          setDoc(null);
          return;
        }
        
        console.log('DocumentEdit: Fetched document data:', docData);
        setDoc(docData);
        
        // Fetch metadata separately
        try {
          const metadataResponse = await DocumentMetadataApi.getDocumentMetadata(docData.id);
          let metadataArray = [];
          
          if (metadataResponse.ok) {
            const metadataData = await metadataResponse.json();
            console.log('DocumentEdit: Fetched metadata:', metadataData);
            const rawMetadata = Array.isArray(metadataData) ? metadataData : (metadataData.metadata || []);
            
            // Remove duplicates based on metadata field name
            const uniqueMetadata = [];
            const seenFields = new Set();
            
            rawMetadata.forEach(meta => {
              const fieldName = meta.metadata_definition?.name || meta.metadataName || `Field #${meta.id}`;
              const normalizedFieldName = fieldName.toLowerCase().trim();
              
              if (seenFields.has(normalizedFieldName)) {
                console.log(`DocumentEdit: Skipping duplicate field: ${fieldName}`);
                return;
              }
              
              seenFields.add(normalizedFieldName);
              uniqueMetadata.push(meta);
            });
            
            metadataArray = uniqueMetadata;
          } else {
            console.warn("Failed to fetch metadata:", metadataResponse.status);
            metadataArray = docData.metadata || [];
          }
          
          console.log('DocumentEdit: Final unique metadata array:', metadataArray);
          setMetadata(metadataArray);
          
        } catch (metadataError) {
          console.error("Error fetching metadata:", metadataError);
          setMetadata([]);
        }
        
      } catch (error) {
        console.error("Error fetching document:", error);
        setDoc(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDocument();
    }
  }, [id]);

  // Fetch file URL when document is loaded
  useEffect(() => {
    if (doc?.id) {
      fetchFileUrl(doc.id);
    }
  }, [doc?.id, fetchFileUrl]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      cleanupFileUrl();
    };
  }, [cleanupFileUrl]);

  // Sort metadata in the same order as DocumentView
  const sortMetadata = (metaArray) => {
    const getIndex = (fieldName) => {
      const i = fieldOrder.findIndex(
        (f) => f.toLowerCase() === fieldName.toLowerCase()
      );
      return i === -1 ? 9999 : i;
    };
    return [...metaArray].sort((a, b) => {
      const nameA = a.metadata_definition?.name || a.metadataName || "";
      const nameB = b.metadata_definition?.name || b.metadataName || "";
      const iA = getIndex(nameA);
      const iB = getIndex(nameB);
      if (iA !== iB) return iA - iB;
      return nameA.localeCompare(nameB);
    });
  };

  // ✅ FIXED: Save function with course association updates
  const handleSaveAll = async () => {
    if (!doc?.id) {
      console.error('DocumentEdit: No document ID available for saving');
      return;
    }

    try {
      setSaving(true);
      console.log('DocumentEdit: Starting save process...');

      // ✅ STEP 1: Save metadata
      const realMetadata = metadata.filter(meta => {
        return typeof meta.id === 'number' && meta.id > 0;
      });

      console.log('DocumentEdit: Updating metadata:', realMetadata.length, 'items');

      if (realMetadata.length > 0) {
        const updatePromises = realMetadata.map(async (meta) => {
          try {
            console.log(`DocumentEdit: Updating metadata ${meta.id} with value:`, meta.value?.substring(0, 100) + '...');
            
            const updateData = {
              documentId: doc.id,
              metadataDefinitionId: meta.metadata_definition?.id || meta.metadataDefinitionId,
              value: meta.value || '',
              confidence: meta.confidence || 100
            };
            
            console.log(`DocumentEdit: Complete API request data for metadata ${meta.id}:`, updateData);
            
            if (!updateData.documentId || !updateData.metadataDefinitionId) {
              console.error(`DocumentEdit: Missing required fields for metadata ${meta.id}:`, {
                documentId: updateData.documentId,
                metadataDefinitionId: updateData.metadataDefinitionId,
                metadataObject: meta
              });
              throw new Error(`Missing required fields for metadata ${meta.id}`);
            }
            
            const response = await DocumentMetadataApi.updateDocumentMetadata(
              meta.id, 
              updateData,
              null
            );
            
            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Failed to update metadata ${meta.id}: ${response.status} - ${errorText}`);
            }
            
            console.log(`DocumentEdit: Successfully updated metadata ${meta.id}`);
            
          } catch (error) {
            console.error(`DocumentEdit: Error updating metadata ${meta.id}:`, error);
            throw error;
          }
        });

        await Promise.all(updatePromises);
        console.log('DocumentEdit: All metadata updates completed successfully');
      }

      // ✅ STEP 2: Handle course association updates
      console.log('DocumentEdit: Updating course associations...');
      console.log('DocumentEdit: Original courses:', originalCourses);
      console.log('DocumentEdit: Selected courses:', selectedCourses);
      
      // Determine which courses to add and remove
      const coursesToAdd = selectedCourses.filter(courseId => 
        !originalCourses.includes(courseId.toString())
      );
      const coursesToRemove = originalCourses.filter(courseId => 
        !selectedCourses.includes(courseId.toString())
      );
      
      console.log('DocumentEdit: Courses to add:', coursesToAdd);
      console.log('DocumentEdit: Courses to remove:', coursesToRemove);
      
      // Remove old associations
      if (coursesToRemove.length > 0) {
        console.log(`DocumentEdit: Removing ${coursesToRemove.length} course associations...`);
        for (const courseId of coursesToRemove) {
          try {
            const response = await DocumentEndeavorApi.removeDocumentFromEndeavor(doc.id, courseId);
            if (!response.ok) {
              const errorText = await response.text();
              console.error(`DocumentEdit: Failed to remove course ${courseId}:`, errorText);
            } else {
              console.log(`DocumentEdit: Successfully removed course ${courseId}`);
            }
          } catch (error) {
            console.error(`DocumentEdit: Error removing course ${courseId}:`, error);
          }
        }
      }
      
      // Add new associations
      if (coursesToAdd.length > 0) {
        console.log(`DocumentEdit: Adding ${coursesToAdd.length} course associations...`);
        
        for (const courseId of coursesToAdd) {
          try {
            const response = await DocumentEndeavorApi.associateDocumentWithEndeavor(doc.id, courseId);
            if (!response.ok) {
              const errorText = await response.text();
              console.error(`DocumentEdit: Failed to associate with course ${courseId}:`, errorText);
            } else {
              console.log(`DocumentEdit: Successfully associated with course ${courseId}`);
            }
          } catch (error) {
            console.error(`DocumentEdit: Error associating with course ${courseId}:`, error);
          }
        }
      }
      
      // Update the original courses to reflect current state
      setOriginalCourses([...selectedCourses]);
      
      console.log('DocumentEdit: Course association updates completed');

      // ✅ STEP 3: Show success message and navigate
      console.log('DocumentEdit: All save operations completed successfully');
      navigate(`/document/${doc.id}`);
      
    } catch (error) {
      console.error('DocumentEdit: Error during save:', error);
      alert(`Error saving document: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Zoom/pan logic
  const onMouseDown = (e) => {
    e.preventDefault();
    setDragging(true);
    setDragStart({ x: e.clientX - pos.x, y: e.clientY - pos.y });
  };
  
  const onMouseMove = (e) => {
    if (!dragging) return;
    e.preventDefault();
    setPos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  
  const onMouseUp = () => setDragging(false);
  const zoomIn = () => setZoom((prev) => prev + 0.1);
  const zoomOut = () => setZoom((prev) => (prev > 0.2 ? prev - 0.1 : prev));

  const handleDownload = useCallback(() => {
    if (fileUrl && doc?.name) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [fileUrl, doc?.name]);

  const handleRetry = useCallback(() => {
    if (doc?.id) {
      console.log('DocumentEdit: Manual retry initiated');
      setFileError(null);
      fetchFileUrl(doc.id, 0);
    }
  }, [doc?.id, fetchFileUrl]);

  if (loading) {
    return (
      <div className="document-view-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading document for editing...</p>
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="document-view-container">
        <div className="empty-state">
          <h3>Document Not Found</h3>
          <p>The requested document could not be found.</p>
          <button className="btn btn-primary" onClick={() => navigate("/documents")}>
            Return to Documents
          </button>
        </div>
      </div>
    );
  }

  const sortedMetadata = sortMetadata(metadata);

  return (
    <div className="document-view-container animate-fade-in-up">
      <button onClick={() => navigate("/documents")} className="back-btn animate-slide-in-left">
        ← Back to Documents
      </button>

      {/* Save/Cancel Actions at the top */}
      <div className="edit-actions">
        <button 
          className="btn btn-primary" 
          onClick={handleSaveAll} 
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="loading-spinner" style={{ width: "16px", height: "16px", margin: "0" }}></div>
              Saving...
            </>
          ) : (
            <>💾 Save Changes</>
          )}
        </button>
        <button 
          className="btn btn-secondary" 
          onClick={() => navigate(`/document/${doc.id}`)}
          disabled={saving}
        >
          ❌ Cancel
        </button>
      </div>

      {/* Enhanced Document preview section */}
      <div className="preview-container">
        <div className="preview-header">
          <h3>📄 {doc.name} <span style={{ fontSize: "1rem", color: "#f59e0b", fontWeight: "600" }}>(Edit Mode)</span></h3>
          <div className="preview-actions">
            <button className="btn btn-secondary" onClick={zoomOut} disabled={fileLoading}>🔍 Zoom Out</button>
            <span style={{ padding: "0.5rem 1rem", background: "#f3f4f6", borderRadius: "8px", fontWeight: "600" }}>
              {(zoom * 100).toFixed(0)}%
            </span>
            <button className="btn btn-secondary" onClick={zoomIn} disabled={fileLoading}>🔍 Zoom In</button>
            <button 
              className="btn btn-success" 
              onClick={handleDownload} 
              disabled={!fileUrl}
            >
              💾 Download
            </button>
            {fileError && (
              <button 
                className="btn btn-warning" 
                onClick={handleRetry}
                disabled={fileLoading}
              >
                🔄 Retry
              </button>
            )}
          </div>
        </div>

        <div
          ref={previewRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={() => setDragging(false)}
          style={{
            cursor: dragging ? "grabbing" : "grab",
            borderRadius: "8px",
            overflow: "hidden",
            background: "#ffffff",
            border: "2px solid #e5e7eb",
            minHeight: "400px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          {fileLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading file preview...</p>
            </div>
          ) : fileError ? (
            <div className="error-state">
              <div className="icon">⚠️</div>
              <h4>Failed to Load Preview</h4>
              <p>{fileError}</p>
              <button className="btn btn-primary" onClick={handleRetry}>
                🔄 Try Again
              </button>
            </div>
          ) : fileUrl && doc.name.toLowerCase().endsWith(".pdf") ? (
            <iframe
              src={fileUrl}
              style={{
                width: "100%",
                height: "600px",
                border: "none",
                transform: `translate(${pos.x}px, ${pos.y}px) scale(${zoom})`,
                transformOrigin: "center"
              }}
              title="PDF Preview"
              onLoad={() => console.log('DocumentEdit: PDF iframe loaded successfully')}
              onError={() => {
                console.error('DocumentEdit: PDF iframe failed to load');
                setFileError('PDF preview failed to load');
              }}
            />
          ) : fileUrl && doc.name.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/) ? (
            <img
              src={fileUrl}
              alt="Document Preview"
              style={{
                width: "100%",
                height: "auto",
                transform: `translate(${pos.x}px, ${pos.y}px) scale(${zoom})`,
                transformOrigin: "center"
              }}
              onError={(e) => {
                console.error('DocumentEdit: Image failed to load');
                setFileError('Image preview failed to load');
              }}
            />
          ) : fileUrl && doc.name.toLowerCase().endsWith('.docx') ? (
            docxConverting ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Converting DOCX to preview...</p>
              </div>
            ) : docxHtml ? (
              <div 
                className="docx-viewer"
                style={{
                  transform: `translate(${pos.x}px, ${pos.y}px) scale(${zoom})`,
                  transformOrigin: "center",
                  width: "100%",
                  height: "100%",
                  overflow: "auto",
                  padding: "20px",
                  backgroundColor: "white"
                }}
              >
                <div 
                  className="docx-content"
                  dangerouslySetInnerHTML={{ __html: docxHtml }}
                />
              </div>
            ) : (
              <div className="file-placeholder">
                <div className="icon">📄</div>
                <h4>DOCX conversion failed</h4>
                <button className="btn btn-primary" onClick={handleDownload}>
                  Download File
                </button>
              </div>
            )
          ) : fileUrl && doc.name.toLowerCase().endsWith('.pptx') ? (
            <div className="file-placeholder">
              <div className="icon">📊</div>
              <h4>{doc.name}</h4>
              <p>PowerPoint preview coming soon</p>
              <p style={{ fontSize: '0.9em', color: '#6b7280', marginTop: '10px' }}>
                We're working on adding PPTX preview support. For now, please download the file to view it.
              </p>
              <button className="btn btn-primary" onClick={handleDownload}>
                Download File
              </button>
            </div>
          ) : fileUrl ? (
            <div className="file-placeholder">
              <div className="icon">📁</div>
              <h4>Cannot preview this file type</h4>
              <button className="btn btn-primary" onClick={handleDownload}>
                Download File
              </button>
            </div>
          ) : (
            <div className="file-placeholder">
              <div className="icon">⏳</div>
              <h4>Loading file preview...</h4>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Metadata section */}
      <section className="metadata-section">
        <h2>✏️ Edit Metadata</h2>
        {sortedMetadata.length === 0 && coursesLoading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading metadata and courses...</p>
          </div>
        ) : (
          <table className="metadata-table">
            <tbody>
              {/* ✅ FIXED: Associated Courses as first row */}
              <tr key="associated-courses">
                <td>Associated Courses</td>
                <td>
                  {coursesLoading ? (
                    <div className="loading-state-inline">
                      <div className="loading-spinner small"></div>
                      <span>Loading courses...</span>
                    </div>
                  ) : (
                    <div className="course-selection-container">
                      {availableCourses.length === 0 ? (
                        <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                          No courses available
                        </p>
                      ) : (
                        <div className="course-checkboxes">
                          {availableCourses.map(course => {
                            const courseId = (course.endeavorId || course.id || course.endeavor_id).toString();
                            return (
                              <label key={courseId} className="course-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={selectedCourses.includes(courseId)}
                                  onChange={(e) => handleCourseSelectionChange(
                                    courseId, 
                                    e.target.checked
                                  )}
                                  className="course-checkbox"
                                />
                                <span className="course-checkbox-text">
                                  {course.name || course.title || `Course ${courseId}`}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                      {selectedCourses.length > 0 && (
                        <div className="selected-courses-preview">
                          <small style={{ color: '#6b7280' }}>
                            {selectedCourses.length} course{selectedCourses.length !== 1 ? 's' : ''} selected
                          </small>
                        </div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
              
              {/* ✅ EXISTING: Regular metadata fields */}
              {sortedMetadata.map((meta, index) => {
                const fieldName = meta.metadata_definition?.name || meta.metadataName || `Field #${meta.id}`;
                const isTagField = fieldName.toLowerCase() === "key topics" || 
                                 fieldName.toLowerCase() === "important names & events" ||
                                 fieldName.toLowerCase() === "important names and events";
                
                return (
                  <tr key={`metadata-${meta.id}-${index}`}>
                    <td>{fieldName}</td>
                    <td>
                      {isTagField ? (
                        <TagEditor
                          value={meta.value || ""}
                          onChange={(newValue) =>
                            setMetadata((prev) =>
                              prev.map((m) =>
                                m.id === meta.id
                                  ? { ...m, value: newValue }
                                  : m
                              )
                            )
                          }
                          placeholder={`Enter ${fieldName.toLowerCase()}...`}
                          fieldName={fieldName}
                        />
                      ) : (
                        <div className="metadata-field-container">
                          <textarea
                            rows={6}
                            value={meta.value || ""}
                            onChange={(e) =>
                              setMetadata((prev) =>
                                prev.map((m) =>
                                  m.id === meta.id
                                    ? { ...m, value: e.target.value }
                                    : m
                                )
                              )
                            }
                            placeholder={`Enter ${fieldName.toLowerCase()}...`}
                          />
                          <div className="confidence-editor" style={{ marginTop: '8px' }}>
                            <label htmlFor={`confidence-${meta.id}`} style={{ fontSize: '0.875rem', color: '#666' }}>
                              Confidence Level:
                            </label>
                            <input
                              id={`confidence-${meta.id}`}
                              type="range"
                              min="0"
                              max="100"
                              value={meta.confidence || 100}
                              onChange={(e) =>
                                setMetadata((prev) =>
                                  prev.map((m) =>
                                    m.id === meta.id
                                      ? { ...m, confidence: parseInt(e.target.value) }
                                      : m
                                  )
                                )
                              }
                              style={{ width: '100%', margin: '4px 0' }}
                            />
                            <span style={{ fontSize: '0.875rem', color: '#666' }}>
                              {meta.confidence || 100}%
                            </span>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

export default DocumentEdit;
